/**
 * @file Navigation desktop centrée du header public.
 * @module packages.common.layouts.public.components.HeaderDesktopNav
 *
 * Affiche les entrées du menu (coups de cœur, tous les bons, catégories,
 * à propos) avec le menu déroulant des catégories au survol. Le déroulant
 * s'appuie sur Popper (ancrage réel sous le déclencheur, animation Grow
 * depuis le haut) avec une fermeture temporisée : un bref passage de la
 * souris hors de la zone ne le referme pas. Les entrées conditionnelles
 * (coups de cœur, catégories) sont masquées quand leurs données sont vides.
 */
import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Box, Grow, List, ListItem, Paper, Popper, Stack, Typography, alpha} from '@mui/material';
import Iconify from 'packages/common/components/iconify';
import HeaderNavItem from './HeaderNavItem';

/** Délai avant fermeture du déroulant : absorbe les trajets de souris imprécis. */
const CLOSE_DELAY_MS = 180;

const HeaderDesktopNav = ({categories = [], hasFeatured, primaryColor, getTranslatedName, onFeaturedClick, onAllGiftsClick, onCategorySelect, onAboutClick}) => {
  const {t} = useTranslation();
  const [showCategories, setShowCategories] = useState(false);
  const categoriesAnchorRef = useRef(null);
  const closeTimerRef = useRef(null);

  const handleCategoriesEnter = () => {
    clearTimeout(closeTimerRef.current);
    setShowCategories(true);
  };

  const handleCategoriesLeave = () => {
    closeTimerRef.current = setTimeout(() => setShowCategories(false), CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const handleCategoryClick = (categoryId) => {
    setShowCategories(false);
    onCategorySelect(categoryId);
  };

  const hasCategories = categories.length > 0;

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        // Pilule de navigation : contenant teinté à la couleur de
        // l'établissement, les entrées deviennent des pastilles blanches
        // au survol
        alignItems: 'center',
        alignSelf: 'center',
        justifySelf: 'center', // Centrage absolu dans la grille du header
        flexWrap: 'nowrap',
        p: 0.5,
        borderRadius: 999,
        backgroundColor: alpha(primaryColor, 0.05),
        border: '1px solid',
        borderColor: alpha(primaryColor, 0.12),
      }}
    >
      {hasFeatured && (
        <HeaderNavItem
          label={t('header.menu.featured')}
          onClick={onFeaturedClick}
          primaryColor={primaryColor}
          icon={<Iconify icon="mdi:heart" width={16} color={primaryColor} />}
        />
      )}

      <HeaderNavItem
        label={t('header.menu.allGifts')}
        onClick={onAllGiftsClick}
        primaryColor={primaryColor}
        icon={<Iconify icon="mdi:gift" width={16} color={primaryColor} />}
      />

      {hasCategories && (
        <Box
          ref={categoriesAnchorRef}
          onMouseEnter={handleCategoriesEnter}
          onMouseLeave={handleCategoriesLeave}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <HeaderNavItem
            label={t('header.menu.categories')}
            primaryColor={primaryColor}
            isActive={showCategories}
            icon={<Iconify icon="mdi:tag-multiple" width={16} color={primaryColor} />}
            endIcon={(
              <Iconify
                icon={showCategories ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                width={20}
              />
            )}
          />

          <Popper
            open={showCategories}
            anchorEl={categoriesAnchorRef.current}
            placement="bottom"
            transition
            sx={{zIndex: (muiTheme) => muiTheme.zIndex.modal}}
            modifiers={[{name: 'offset', options: {offset: [0, 6]}}]}
          >
            {({TransitionProps}) => (
              <Grow {...TransitionProps} timeout={180} style={{transformOrigin: 'top center'}}>
                <Paper
                  onMouseEnter={handleCategoriesEnter}
                  onMouseLeave={handleCategoriesLeave}
                  elevation={0}
                  sx={{
                    minWidth: 264,
                    maxWidth: 340,
                    p: 1,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      px: 1.5,
                      pt: 0.5,
                      pb: 0.25,
                      color: 'text.disabled',
                      letterSpacing: '0.1em',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      lineHeight: 2,
                    }}
                  >
                    {t('header.menu.categories')}
                  </Typography>

                  <List disablePadding>
                    {categories.map((category) => (
                      <ListItem
                        key={category._id}
                        onClick={() => handleCategoryClick(category._id)}
                        sx={{
                          'borderRadius': 2,
                          'px': 1.25,
                          'py': 1,
                          'gap': 1.25,
                          'cursor': 'pointer',
                          'transition': 'background-color 0.15s ease',
                          '&:hover': {
                            'backgroundColor': alpha(primaryColor, 0.07),
                            '& .categoryArrow': {
                              opacity: 1,
                              transform: 'translateX(0)',
                            },
                            '& .categoryChip': {
                              backgroundColor: alpha(primaryColor, 0.18),
                            },
                          },
                        }}
                      >
                        <Box
                          className="categoryChip"
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(primaryColor, 0.1),
                            transition: 'background-color 0.15s ease',
                            flexShrink: 0,
                          }}
                        >
                          <Iconify icon="mdi:tag-outline" width={15} color={primaryColor} />
                        </Box>

                        <Typography variant="body2" sx={{fontWeight: 500, flex: 1, color: 'text.primary'}}>
                          {getTranslatedName(category)}
                        </Typography>

                        <Iconify
                          className="categoryArrow"
                          icon="mdi:arrow-right"
                          width={16}
                          color={primaryColor}
                          sx={{
                            opacity: 0,
                            transform: 'translateX(-4px)',
                            transition: 'opacity 0.18s ease, transform 0.18s ease',
                            flexShrink: 0,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      )}

      <HeaderNavItem
        label={t('header.menu.about')}
        onClick={onAboutClick}
        primaryColor={primaryColor}
        icon={<Iconify icon="mdi:information" width={16} color={primaryColor} />}
      />
    </Stack>
  );
};

HeaderDesktopNav.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
  })),
  hasFeatured: PropTypes.bool.isRequired,
  primaryColor: PropTypes.string.isRequired,
  getTranslatedName: PropTypes.func.isRequired,
  onFeaturedClick: PropTypes.func.isRequired,
  onAllGiftsClick: PropTypes.func.isRequired,
  onCategorySelect: PropTypes.func.isRequired,
  onAboutClick: PropTypes.func.isRequired,
};

export default HeaderDesktopNav;
